import React from 'react';
import DOMPurify from 'dompurify';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { RELEASES } from '@/data/releaseNotes';
import { useBrandName } from '@/lib/branding';

// Renders **bold** inline markdown only — content is sanitized.
const renderInline = (text) => {
  const html = text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>');
  return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
};

// "2026-10-16" → "October 2026" (falls back to the raw value).
const prettyBuildDate = (d) => {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})/.exec(String(d));
  if (!m) return d;
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  return `${months[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

export default function ReleaseNotesDialog({ open, onOpenChange, currentVersion, buildDate }) {
  const brandName = useBrandName();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="release-notes-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50">
          <DialogTitle className="flex items-center gap-2 text-lg font-heading">
            <Sparkles className="w-5 h-5 text-primary" />
            What's new in {brandName}
          </DialogTitle>
          {currentVersion && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="release-running-version">
              You're running <span className="text-foreground font-semibold">v{currentVersion}</span>
              {buildDate ? <> · built {buildDate}</> : null}
            </p>
          )}
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="px-6 py-5 space-y-8" data-testid="release-notes-body">
            {RELEASES.map((rel, idx) => {
              // The newest card represents the CURRENT release: stamp its version
              // and date from the backend build metadata so they can never drift
              // from the running build (the hardcoded content can lag a deploy).
              const isLatest = idx === 0;
              const shownVersion = isLatest && currentVersion ? currentVersion : rel.version;
              const shownDate = isLatest && buildDate ? prettyBuildDate(buildDate) : rel.date;
              return (
              <div key={rel.version} data-testid={`release-${rel.version}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-primary/15 text-primary border-primary/20 text-xs">v{shownVersion}</Badge>
                  <span className="text-xs text-muted-foreground">{shownDate}</span>
                </div>
                <h3 className="text-base font-heading font-bold text-foreground">{rel.headline}</h3>
                {rel.summary && (
                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">{renderInline(rel.summary)}</p>
                )}
                <div className="mt-4 space-y-5">
                  {rel.groups.map((g) => (
                    <div key={g.title}>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/80 mb-2">{g.title}</p>
                      <ul className="space-y-2">
                        {g.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            <span>{renderInline(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );})}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
